import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  IconButton,
  Badge,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Send,
  Search,
  MoreVert,
} from '@mui/icons-material';
import { useMessages, useConversation } from '../hooks/useMessages';
import { messageService, Conversation, Message, IceBreakerTemplate } from '../services/messageService';
import IceBreakerPicker from '../components/messages/IceBreakerPicker';
import { formatDistanceToNow } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  ContactInfoRedactedNotice,
  RedactedMessageContent,
} from '../components/MessageRedaction';

const emitDebugLog = (payload: {
  runId: string;
  hypothesisId: string;
  location: string;
  message: string;
  data: Record<string, unknown>;
}) => {
  const eventPayload = {
    sessionId: '654aec',
    runId: payload.runId,
    hypothesisId: payload.hypothesisId,
    location: payload.location,
    message: payload.message,
    data: payload.data,
    timestamp: Date.now(),
  };

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([JSON.stringify(eventPayload)], { type: 'application/json' });
    navigator.sendBeacon('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d', blob);
  }

  fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Debug-Session-Id': '654aec',
    },
    body: JSON.stringify(eventPayload),
  }).catch(() => {});
};

const MessagesPage: React.FC = () => {
  const { conversations, loading, error, unreadCount, markAsRead, refresh } = useMessages();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const { state } = useApp();
  const initializedProfileConversationFor = useRef<string | null>(null);

  useEffect(() => {
    // #region agent log
    emitDebugLog({
      runId: 'initial',
      hypothesisId: 'H0',
      location: 'web/src/pages/MessagesPage.tsx:mount',
      message: 'MessagesPage mounted',
      data: { pathname: window.location.pathname },
    });
    // #endregion
  }, []);

  useEffect(() => {
    // #region agent log
    emitDebugLog({
      runId: 'initial',
      hypothesisId: 'H4',
      location: 'web/src/pages/MessagesPage.tsx:selectedConversationEffect',
      message: 'Selected conversation changed at page level',
      data: {
        selectedConversationId: selectedConversation?.id ?? null,
        conversationCount: conversations.length,
      },
    });
    // #endregion
  }, [selectedConversation?.id, conversations.length]);

  // Check if we need to open a specific conversation
  useEffect(() => {
    const initializeConversationFromProfileEntry = async () => {
      const userId = searchParams.get('userId');
      if (!userId) return;

      const existingConversation = conversations.find((conv) => conv.otherUser.id === userId);
      if (existingConversation) {
        initializedProfileConversationFor.current = userId;
        setSelectedConversation(existingConversation);
        return;
      }

      if (initializedProfileConversationFor.current === userId) {
        return;
      }

      try {
        initializedProfileConversationFor.current = userId;
        await messageService.createConversation(userId);
        await refresh();
      } catch (conversationError) {
        initializedProfileConversationFor.current = null;
        console.error('Unable to create/resume conversation from profile entry', conversationError);
      }
    };

    void initializeConversationFromProfileEntry();
  }, [searchParams, conversations, refresh]);

  const handleSelectConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    if (conversation.unreadCount > 0) {
      await markAsRead(conversation.id);
    }
  };

  const filteredConversations = conversations.filter(conv =>
    `${conv.otherUser.firstName} ${conv.otherUser.lastName}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4, height: 'calc(100vh - 100px)' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Messages
        {unreadCount > 0 && (
          <Badge badgeContent={unreadCount} color="primary" sx={{ ml: 2 }} />
        )}
      </Typography>

      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Conversations List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Divider />
            
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
            ) : (
              <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                {filteredConversations.map((conversation) => (
                  <ListItem
                    key={conversation.id}
                    button
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => handleSelectConversation(conversation)}
                    sx={{
                      backgroundColor: conversation.unreadCount > 0 ? 'action.hover' : 'inherit',
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        badgeContent={conversation.unreadCount}
                        color="primary"
                        overlap="circular"
                      >
                        <Avatar src={conversation.otherUser.avatar || undefined}>
                          {conversation.otherUser.firstName?.[0]}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between">
                          <Typography
                            variant="subtitle2"
                            fontWeight={conversation.unreadCount > 0 ? 700 : 400}
                          >
                            {conversation.otherUser.firstName} {conversation.otherUser.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {conversation.lastMessage &&
                              formatDistanceToNow(new Date(conversation.lastMessage.createdAt), {
                                addSuffix: true,
                              })}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          fontWeight={conversation.unreadCount > 0 ? 600 : 400}
                        >
                          {conversation.lastMessage?.content || 'No messages yet'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                
                {filteredConversations.length === 0 && (
                  <Box p={4} textAlign="center">
                    <Typography color="text.secondary">
                      No conversations found
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Message Thread */}
        <Grid item xs={12} md={8}>
          {selectedConversation ? (
            <MessageThread
              conversation={selectedConversation}
              currentUserId={state.user?.id ?? null}
            />
          ) : (
            <Paper sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">
                Select a conversation to start messaging
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

// Message Thread Component
const MessageThread: React.FC<{ conversation: Conversation; currentUserId: string | null }> = ({
  conversation,
  currentUserId,
}) => {
  const { messages, loading, refresh } = useConversation(conversation.id);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [contactInfoRedacted, setContactInfoRedacted] = useState(false);
  const [iceBreakers, setIceBreakers] = useState<IceBreakerTemplate[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    messageService
      .getIceBreakers()
      .then((templates) => {
        if (mounted) {
          setIceBreakers(templates);
        }
      })
      .catch((err) => {
        console.error('Failed to load ice-breaker templates:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // #region agent log
    emitDebugLog({
      runId: 'initial',
      hypothesisId: 'H1',
      location: 'web/src/pages/MessagesPage.tsx:messageThreadStateEffect',
      message: 'MessageThread state snapshot',
      data: {
        conversationId: conversation.id,
        messageCount: messages.length,
        loading,
      },
    });
    // #endregion
  }, [conversation.id, messages.length, loading]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      const { meta } = await messageService.sendMessage({
        recipientId: conversation.otherUser.id,
        content: newMessage.trim(),
      });
      setContactInfoRedacted(meta.contactInfoRedacted);
      setNewMessage('');
      await refresh();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Avatar src={conversation.otherUser.avatar || undefined} sx={{ mr: 2 }}>
              {conversation.otherUser.firstName?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6">
                {conversation.otherUser.firstName} {conversation.otherUser.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {conversation.otherUser.role}
              </Typography>
            </Box>
          </Box>
          <IconButton>
            <MoreVert />
          </IconButton>
        </Box>
      </Box>

      {/* Messages */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {messages.map((message) => {
              const isOwn = message.senderId === currentUserId;
              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    mb: 2,
                  }}
                >
                  <Paper
                    sx={{
                      p: 1.5,
                      maxWidth: '70%',
                      backgroundColor: isOwn ? 'primary.main' : 'grey.100',
                      color: isOwn ? 'primary.contrastText' : 'text.primary',
                    }}
                  >
                    <RedactedMessageContent
                      content={message.content}
                      color={isOwn ? 'primary.contrastText' : 'text.primary'}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        opacity: 0.7,
                      }}
                    >
                      {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                      {isOwn && message.isRead && ' • Read'}
                    </Typography>
                  </Paper>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <ContactInfoRedactedNotice visible={contactInfoRedacted} />
        <IceBreakerPicker
          templates={iceBreakers}
          visible={!loading && messages.length === 0}
          onSelect={(body) => setNewMessage(body)}
        />
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => {
            const nextValue = e.target.value;
            // #region agent log
            emitDebugLog({
              runId: 'initial',
              hypothesisId: 'H1',
              location: 'web/src/pages/MessagesPage.tsx:newMessage:onChange',
              message: 'Composer input changed',
              data: {
                conversationId: conversation.id,
                nextMessageLength: nextValue.length,
                visibleMessageCount: messages.length,
                loading,
              },
            });
            // #endregion
            if (contactInfoRedacted) {
              setContactInfoRedacted(false);
            }
            setNewMessage(nextValue);
          }}
          onKeyPress={handleKeyPress}
          disabled={sending}
          inputProps={{ 'data-testid': 'message-composer-input' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  data-testid="message-send-button"
                  aria-label="Send message"
                >
                  {sending ? <CircularProgress size={24} /> : <Send />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Paper>
  );
};

export default MessagesPage;

